/**
 * Append-only route registry (docs/AGENT_PROTOCOL.md). Feature agents append `RouteObject`s here with ONE
 * atomic edit (or export `<name>Routes` from their folder and append one spread line). The shell mounts every
 * entry inside the authenticated <AppShell>; set `handle.permission` and the shell wraps it in
 * <RequirePermission>. Paths are relative to `/` (e.g. `claims`, `claims/:id`). A registry route with the same
 * path as a shell placeholder (`draws`, `winners`, `claims`, `finance`, `fulfillment`, `audience`, `catalog`,
 * `sponsor-reports`, `audit`, `settings`) replaces the placeholder. Wizard steps are replaced by appending to
 * `wizardStepRoutes` (see docs/modules/console.md "Wizard slice contract").
 *
 * Example:
 *   export const claimsRoutes: ConsoleRoute[] = [
 *     { path: 'claims', handle: { permission: 'claims.view', title: 'Claims queue' }, element: <ClaimsQueuePage /> },
 *   ];
 *   // in this file: ...claimsRoutes,
 */
import type { Permission } from '@windrop/contracts';
import type { RouteObject } from 'react-router';

export interface ConsoleRouteHandle {
  /** ANY of these permissions opens the route; otherwise the shell redirects to /forbidden. */
  permission?: Permission | readonly Permission[];
  title?: string;
}

export type ConsoleRoute = RouteObject & { handle?: ConsoleRouteHandle };

import { winnersRoutes } from './winners/routes';
import { supportRoutes } from './support/routes';
import { analyticsRoutes } from './analytics-routes';
import { catalogRoutes } from './catalog/routes';
import { sponsorReportsRoutes } from './sponsor-reports/routes';
import { drawsRoutes } from './draws/routes';
import { financeRoutes } from './finance/routes';
import { claimsRoutes } from './claims/routes';
import { settingsRoutes } from './settings/routes';
import { fulfillmentRoutes } from './fulfillment/routes';
import { entriesRoutes } from './entries/routes';
export const routes: ConsoleRoute[] = [
  ...claimsRoutes,
  ...fulfillmentRoutes,
  ...financeRoutes,
  ...drawsRoutes,
  ...catalogRoutes,
  ...sponsorReportsRoutes,
  ...analyticsRoutes,
  ...winnersRoutes,
  ...supportRoutes,
  ...settingsRoutes,
  ...entriesRoutes,
];

/**
 * Wizard step overrides keyed by step id (`prizes`, `sponsors`, `engagement`, `rules`, `experience`, `claims`).
 * Feature agents append `{ step: 'prizes', element: <PrizesStep /> }`; the frame renders it instead of the stub.
 */
export interface WizardStepRoute {
  step: 'prizes' | 'sponsors' | 'engagement' | 'rules' | 'experience' | 'claims';
  element: React.ReactNode;
}
import { EngagementStep } from './lotteries/wizard/EngagementStep';
import { catalogWizardSteps } from './catalog/wizard/steps';
import { ClaimsStep } from './lotteries/wizard/ClaimsStep';
import { ExperienceStep } from './lotteries/wizard/ExperienceStep';
import { RulesStep } from './lotteries/wizard/RulesStep';
export const wizardStepRoutes: WizardStepRoute[] = [
  { step: 'rules', element: <RulesStep /> },
  { step: 'experience', element: <ExperienceStep /> },
  { step: 'claims', element: <ClaimsStep /> },
  ...catalogWizardSteps,
  { step: 'engagement', element: <EngagementStep /> },
];
